import { useEffect, useRef } from "react";
import { useAutosave } from "react-autosave";
import { type FieldValues, useFormContext } from "react-hook-form";

export interface AutosaveWrapperProps<T extends FieldValues = FieldValues> {
  onSave: (values: T) => Promise<void>;
  isPaused: boolean;
  interval?: number;
}

export function AutosaveWrapper<T extends FieldValues = FieldValues>({
  onSave,
  isPaused,
  interval = 1500,
}: AutosaveWrapperProps<T>) {
  const { watch, getValues } = useFormContext<T>();
  const formValues = watch();
  const prevIsPausedRef = useRef(isPaused);

  useAutosave({
    data: formValues,
    onSave,
    interval,
    saveOnUnmount: false,
  });

  useEffect(() => {
    const wasPaused = prevIsPausedRef.current;
    const isNowActive = !isPaused;

    if (wasPaused && isNowActive) {
      // Small delay to ensure workflow editor sync completes first
      setTimeout(() => onSave(getValues()), 0);
    }

    prevIsPausedRef.current = isPaused;
  }, [isPaused, onSave, getValues]);

  return null;
}
